/** Settings → Users & Roles. RBAC center. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ComingOnline, THead } from "./shared";
import {
  useAdminUsers, useAdminRoles, useInviteUser, useUpdateUser, useDeleteUser,
  type MfaMethod, type AdminUser,
} from "@/hooks/security/useEdrAdmin";

export function UsersRolesSection() {
  const usersQ = useAdminUsers();
  const rolesQ = useAdminRoles();
  const [inviteOpen, setInviteOpen] = useState(false);

  const usersUnavail = usersQ.data?.kind === "unavailable";
  const rolesUnavail = rolesQ.data?.kind === "unavailable";
  const users = usersQ.data?.kind === "ok" ? usersQ.data.data.users : [];
  const roles = rolesQ.data?.kind === "ok" ? rolesQ.data.data.roles : [];

  return (
    <div className="space-y-4">
      <Panel
        title={`Users (${users.length})`}
        actions={
          <Button size="sm" disabled={usersUnavail || roles.length === 0} onClick={() => setInviteOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Invite user
          </Button>
        }
        loading={usersQ.isLoading}
        isError={usersQ.isError}
        error={usersQ.error}
        onRetry={() => usersQ.refetch()}
        empty={!usersQ.isLoading && (usersUnavail || users.length === 0)}
        emptyTitle={usersUnavail ? "Coming online — Q3 2026" : "No users yet"}
        emptyDescription={usersUnavail ? "User management endpoints aren't live yet." : "Invite teammates to get started."}
      >
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-xs">
            <THead cols={["User", "Role", "MFA", "Last activity", ""]} />
            <tbody>
              {users.map((u) => <UserRow key={u.id} user={u} roles={roles} />)}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={`Roles (${roles.length})`} loading={rolesQ.isLoading}>
        {rolesUnavail ? <ComingOnline /> : (
          <div className="overflow-x-auto -mx-4 -my-4">
            <table className="w-full text-xs">
              <THead cols={["Role", "Permissions", "Users"]} />
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id} className="border-b border-border/60">
                    <td className="px-3 py-2">
                      <div className="font-medium text-foreground">{r.name}{r.builtIn && <span className="ml-1.5 text-[10px] text-muted-foreground">built-in</span>}</div>
                      <div className="text-[11px] text-muted-foreground">{r.description}</div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      <div className="flex flex-wrap gap-1">
                        {r.permissions.slice(0, 4).map((p) => (
                          <code key={p} className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">{p}</code>
                        ))}
                        {r.permissions.length > 4 && <span className="text-[10px]">+{r.permissions.length - 4}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.userCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} roles={roles} />
    </div>
  );
}

function UserRow({ user, roles }: { user: AdminUser; roles: { id: string; name: string }[] }) {
  const update = useUpdateUser();
  const del = useDeleteUser();
  const mfaIcon = user.mfaMethod === "none"
    ? <span className="inline-flex items-center gap-1 text-destructive"><ShieldOff className="h-3 w-3" />off</span>
    : <span className="inline-flex items-center gap-1 text-success"><ShieldCheck className="h-3 w-3" />{user.mfaMethod.toUpperCase()}</span>;
  return (
    <tr className="border-b border-border/60 hover:bg-muted/40">
      <td className="px-3 py-2">
        <div className="font-medium text-foreground">{user.displayName || user.email}</div>
        {user.displayName && <div className="text-[11px] text-muted-foreground">{user.email}</div>}
      </td>
      <td className="px-3 py-2">
        <Select value={user.roleId} onValueChange={(v) => update.mutate({ id: user.id, roleId: v })}>
          <SelectTrigger className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 text-xs">{mfaIcon}</td>
      <td className="px-3 py-2 text-muted-foreground tabular-nums">
        {user.lastSeenAt ? formatDistanceToNow(new Date(user.lastSeenAt), { addSuffix: true }) : "never"}
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={del.isPending}
          onClick={() => { if (confirm(`Remove ${user.email}?`)) del.mutate(user.id); }}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function InviteDialog({ open, onOpenChange, roles }: { open: boolean; onOpenChange: (o: boolean) => void; roles: { id: string; name: string }[] }) {
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(roles[0]?.id ?? "");
  const [sendEmail, setSendEmail] = useState(true);
  const invite = useInviteUser();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
          <DialogDescription>Send an invite link that expires in 7 days.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger><SelectValue placeholder="Pick a role" /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={sendEmail} onCheckedChange={(c) => setSendEmail(!!c)} /> Email the invite link
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!email || !roleId || invite.isPending} onClick={async () => {
            await invite.mutateAsync({ email, roleId, sendEmail });
            onOpenChange(false); setEmail("");
          }}>{invite.isPending ? "Sending…" : "Send invite"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
